import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Shield, Users, FileText, Stethoscope, TrendingUp, Trash2, 
  AlertTriangle, DollarSign, Activity 
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { superAdminAPI } from '../api';
import axios from 'axios';

const SuperAdminDashboard = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [consultings, setConsultings] = useState([]);
  const [userFilter, setUserFilter] = useState('all');

  useEffect(() => {
    if (!token) {
      toast({
        title: "Accès refusé",
        description: "Token super admin manquant",
        variant: "destructive"
      });
      navigate('/');
      return;
    }

    verifyAndLoadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verifyAndLoadData = async () => {
    try {
      // Verify token first
      await superAdminAPI.verifyToken(token);
      
      // Load all data after verification succeeds
      const [statsData, usersData, ordersData, consultingsData] = await Promise.all([
        superAdminAPI.getStats(token),
        superAdminAPI.getUsers(token, userFilter !== 'all' ? userFilter : null),
        superAdminAPI.getOrders(token),
        superAdminAPI.getConsultings(token)
      ]);
      
      setStats(statsData);
      setUsers(usersData);
      setOrders(ordersData);
      setConsultings(consultingsData);
      setLoading(false);
    } catch (error) {
      console.error('Super Admin access error:', error);
      toast({
        title: "Accès refusé",
        description: "Token super admin invalide",
        variant: "destructive"
      });
      navigate('/');
    }
  };

  const loadUsers = async () => {
    try {
      const filter = userFilter !== 'all' ? userFilter : null;
      const data = await superAdminAPI.getUsers(token, filter);
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadStats = async () => {
    try {
      const data = await superAdminAPI.getStats(token);
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadOrders = async () => {
    try {
      const data = await superAdminAPI.getOrders(token);
      setOrders(data);
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const loadConsultings = async () => {
    try {
      const data = await superAdminAPI.getConsultings(token);
      setConsultings(data);
    } catch (error) {
      console.error('Error loading consultings:', error);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`⚠️ ATTENTION !\n\nVoulez-vous VRAIMENT supprimer définitivement l'utilisateur ${userEmail} ?\n\nCette action est IRRÉVERSIBLE et supprimera :\n- Le compte utilisateur\n- Toutes ses commandes\n- Tous ses consultings\n- Tous ses messages\n\nTapez "SUPPRIMER" pour confirmer.`)) {
      return;
    }

    const confirmation = window.prompt('Tapez "SUPPRIMER" pour confirmer :');
    if (confirmation !== "SUPPRIMER") {
      toast({ title: "Annulé", description: "Suppression annulée" });
      return;
    }

    try {
      await superAdminAPI.deleteUser(token, userId);
      toast({
        title: "Utilisateur supprimé",
        description: `${userEmail} a été définitivement supprimé`
      });
      loadUsers();
      loadStats();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'utilisateur",
        variant: "destructive"
      });
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm(`Supprimer définitivement la commande ${orderId} ?`)) return;

    try {
      await superAdminAPI.deleteOrder(token, orderId);
      toast({ title: "Commande supprimée" });
      loadOrders();
      loadStats();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer la commande",
        variant: "destructive"
      });
    }
  };

  const handleDeleteConsulting = async (consultingId) => {
    if (!window.confirm(`Supprimer définitivement le consulting ${consultingId} ?`)) return;

    try {
      await superAdminAPI.deleteConsulting(token, consultingId);
      toast({ title: "Consulting supprimé" });
      loadConsultings();
      loadStats();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le consulting",
        variant: "destructive"
      });
    }
  };

  const handleCleanupDuplicates = async () => {
    if (!window.confirm("Nettoyer les comptes utilisateurs en double ?\n\nCette action va détecter et supprimer tous les comptes avec des emails identiques (case-insensitive), en conservant uniquement le plus récent.\n\nCette action est irréversible.")) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/superadmin/cleanup-duplicates`,
        {},
        { headers: { 'X-Super-Admin-Token': token } }
      );
      
      const { duplicates_removed, details } = response.data;
      
      if (duplicates_removed === 0) {
        toast({
          title: "Aucun duplicata trouvé",
          description: "La base de données ne contient aucun compte en double"
        });
      } else {
        toast({
          title: "Nettoyage terminé",
          description: `${duplicates_removed} compte(s) en double supprimé(s)`
        });
      }
      
      loadStats();
      loadUsers();
    } catch (error) {
      console.error('Cleanup error:', error);
      toast({
        title: "Erreur",
        description: "Erreur lors du nettoyage des duplicatas",
        variant: "destructive"
      });
    }
  };

  const handleResetDatabase = async () => {
    const firstConfirm = window.confirm(
      "⚠️ DANGER EXTRÊME ⚠️\n\n" +
      "Vous êtes sur le point de SUPPRIMER TOUTES LES DONNÉES de la plateforme :\n\n" +
      "- TOUS les utilisateurs\n" +
      "- TOUTES les commandes\n" +
      "- TOUS les consultings\n" +
      "- TOUS les messages\n" +
      "- TOUS les fichiers\n\n" +
      "Cette action est IRRÉVERSIBLE et supprimera DÉFINITIVEMENT toutes les données.\n\n" +
      "Voulez-vous continuer ?"
    );
    
    if (!firstConfirm) return;

    const confirmation = window.prompt(
      'Cette action est DANGEREUSE et IRRÉVERSIBLE.\n\n' +
      'Tapez exactement "SUPPRIMER TOUT" pour confirmer :'
    );
    
    if (confirmation !== "SUPPRIMER TOUT") {
      toast({ 
        title: "Annulé", 
        description: "Reset de la base de données annulé" 
      });
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/superadmin/reset-database?confirm=true`,
        {},
        { headers: { 'X-Super-Admin-Token': token } }
      );
      
      const { deleted_counts } = response.data;
      
      toast({
        title: "Base de données réinitialisée",
        description: `${deleted_counts.users} utilisateurs, ${deleted_counts.orders} commandes, ${deleted_counts.files} fichiers supprimés`,
        variant: "destructive"
      });
      
      // Reload all data
      loadStats();
      loadUsers();
      loadOrders();
      loadConsultings();
    } catch (error) {
      console.error('Reset error:', error);
      toast({
        title: "Erreur",
        description: "Erreur lors du reset de la base de données",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <p className="text-white">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111111] py-12 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="h-8 w-8 text-[#C00000]" />
            <h1 className="text-4xl font-bold">
              <span className="text-white">SUPER</span>{' '}
              <span className="text-[#C00000]">ADMIN</span>
            </h1>
          </div>
          <p className="text-[#A6A6A6]">Gestion complète de la plateforme GT ENGINEERING</p>
          <div className="mt-2 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded">
            <p className="text-yellow-500 text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Accès Super Admin - Toutes les actions sont irréversibles et tracées
            </p>
          </div>
          
          <div className="mt-4 flex flex-wrap gap-3">
            <Button
              onClick={handleCleanupDuplicates}
              variant="outline"
              className="border-yellow-500 text-yellow-500 hover:bg-yellow-500/10"
            >
              🔧 Nettoyer les duplicatas
            </Button>
            <Button
              onClick={() => window.open(`/super-admin/file-manager?token=${token}`, '_blank')}
              variant="outline"
              className="border-[#A6A6A6] text-[#A6A6A6] hover:bg-[#A6A6A6]/10"
            >
              📁 Gestionnaire de fichiers
            </Button>
            <Button
              onClick={handleResetDatabase}
              variant="outline"
              className="border-red-600 text-red-600 hover:bg-red-600/10"
            >
              ⚠️ Reset Base de Données
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A6A6A6] text-sm">Utilisateurs</p>
                    <p className="text-3xl font-bold text-white">{stats.total_users}</p>
                    <p className="text-xs text-[#A6A6A6] mt-1">
                      {stats.total_clients} clients, {stats.total_engineers} ingénieurs
                    </p>
                  </div>
                  <Users className="h-8 w-8 text-[#C00000]" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A6A6A6] text-sm">Commandes</p>
                    <p className="text-3xl font-bold text-white">{stats.total_orders}</p>
                    <p className="text-xs text-[#A6A6A6] mt-1">
                      {stats.pending_orders} en cours, {stats.completed_orders} terminées
                    </p>
                  </div>
                  <FileText className="h-8 w-8 text-[#C00000]" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A6A6A6] text-sm">Consultings</p>
                    <p className="text-3xl font-bold text-white">{stats.total_consultings}</p>
                  </div>
                  <Stethoscope className="h-8 w-8 text-[#C00000]" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#A6A6A6] text-sm">Revenu Total</p>
                    <p className="text-3xl font-bold text-green-500">{stats.total_revenue.toFixed(2)}€</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="users" className="w-full">
          <TabsList className="bg-[#1a1a1a] mb-6">
            <TabsTrigger value="users" className="data-[state=active]:bg-[#C00000]">
              <Users className="h-4 w-4 mr-2" />
              Utilisateurs
            </TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-[#C00000]">
              <FileText className="h-4 w-4 mr-2" />
              Commandes
            </TabsTrigger>
            <TabsTrigger value="consultings" className="data-[state=active]:bg-[#C00000]">
              <Stethoscope className="h-4 w-4 mr-2" />
              Consultings
            </TabsTrigger>
          </TabsList>

          {/* USERS TAB */}
          <TabsContent value="users">
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <span>Gestion des Utilisateurs</span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={userFilter === 'all' ? 'default' : 'outline'}
                      className={userFilter === 'all' ? 'bg-[#C00000]' : 'border-[#A6A6A6]'}
                      onClick={() => { setUserFilter('all'); loadUsers(); }}
                    >
                      Tous
                    </Button>
                    <Button
                      size="sm"
                      variant={userFilter === 'client' ? 'default' : 'outline'}
                      className={userFilter === 'client' ? 'bg-[#C00000]' : 'border-[#A6A6A6]'}
                      onClick={() => { setUserFilter('client'); loadUsers(); }}
                    >
                      Clients
                    </Button>
                    <Button
                      size="sm"
                      variant={userFilter === 'engineer' ? 'default' : 'outline'}
                      className={userFilter === 'engineer' ? 'bg-[#C00000]' : 'border-[#A6A6A6]'}
                      onClick={() => { setUserFilter('engineer'); loadUsers(); }}
                    >
                      Ingénieurs
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {users.map((user) => (
                    <div key={user.id} className="bg-[#111111] p-4 rounded-lg border border-[#A6A6A6]/20">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-white font-semibold">{user.name}</h3>
                            <Badge className={user.role === 'engineer' ? 'bg-blue-500/20 text-blue-500' : 'bg-green-500/20 text-green-500'}>
                              {user.role}
                            </Badge>
                          </div>
                          <p className="text-[#A6A6A6] text-sm">{user.email}</p>
                          <p className="text-[#A6A6A6] text-sm">{user.phone}</p>
                          <div className="flex gap-4 mt-2 text-xs text-[#A6A6A6]">
                            <span>📦 {user.orders_count} commandes</span>
                            <span>🩺 {user.consultings_count} consultings</span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-500 text-red-500 hover:bg-red-500/10"
                          onClick={() => handleDeleteUser(user.email, user.email)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ORDERS TAB */}
          <TabsContent value="orders">
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardHeader>
                <CardTitle className="text-white">Toutes les Commandes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {orders.slice(0, 50).map((order) => (
                    <div key={order.order_id} className="flex justify-between items-center bg-[#111111] p-3 rounded hover:bg-[#1a1a1a] transition-colors cursor-pointer"
                         onClick={() => navigate(`/super-admin/orders/${order.order_id}?token=${token}`)}>
                      <div>
                        <span className="text-white font-semibold">{order.order_id}</span>
                        <span className="text-[#A6A6A6] text-sm ml-3">{order.client_name}</span>
                        <Badge className="ml-2">{order.status}</Badge>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:bg-red-500/10"
                        onClick={(e) => { e.stopPropagation(); handleDeleteOrder(order.order_id); }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CONSULTINGS TAB */}
          <TabsContent value="consultings">
            <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
              <CardHeader>
                <CardTitle className="text-white">Tous les Consultings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {consultings.slice(0, 50).map((consulting) => (
                    <div key={consulting.consulting_id} className="flex justify-between items-center bg-[#111111] p-3 rounded hover:bg-[#1a1a1a] transition-colors cursor-pointer"
                         onClick={() => navigate(`/super-admin/consultings/${consulting.consulting_id}?token=${token}`)}>
                      <div>
                        <span className="text-white font-semibold">{consulting.consulting_id}</span>
                        <span className="text-[#A6A6A6] text-sm ml-3">{consulting.client_name}</span>
                        <Badge className="ml-2">{consulting.status}</Badge>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:bg-red-500/10"
                        onClick={(e) => { e.stopPropagation(); handleDeleteConsulting(consulting.consulting_id); }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
